import { useAuth } from '../auth/useAuth'
import { useLocation } from 'preact-iso'
import type { UserCompanyRoleSetViewDto, UserProfileCompleteViewDto } from '@/types/user'
import type { ClientWorkspaceSummaryViewDto } from '@/types/company'
import type { WorkspaceRolesViewDto, RoleType } from '@/types/userRoles'

export interface UseUserProfileReturn {
  // User profile data
  userProfile?: UserProfileCompleteViewDto | null
  userCompanyRoleSets?: UserCompanyRoleSetViewDto[] | null
  allWorkspaces?: ClientWorkspaceSummaryViewDto[] | null
  homeClientCompanyId?: string | null
  canManageCustomers?: boolean

  // Company context - returns role set for current company from URL
  getCurrentCompanyRoleSet: () => UserCompanyRoleSetViewDto | null
  getCurrentCompanyWorkspaces: () => ClientWorkspaceSummaryViewDto[]

  // Convenience getters (now require companyId)
  isUserInRole: (role: RoleType, companyId: string, workspaceId?: string) => boolean
  getUserRoles: (companyId: string, workspaceId?: string) => RoleType[]
  hasCompanyRole: (role: RoleType, companyId: string) => boolean
  hasWorkspaceRole: (role: RoleType, workspaceId: string, companyId: string) => boolean

  // Workspace capability helpers (server-computed)
  getWorkspaceCapabilities: (clientCompanyId: string, workspaceId: string) => { canAdmin: boolean; canEdit: boolean; canView: boolean }

  // Workspace helpers
  getWorkspaceById: (workspaceId: string) => ClientWorkspaceSummaryViewDto | undefined
  getUserWorkspaces: (companyId: string) => ClientWorkspaceSummaryViewDto[]
}

/**
 * Hook to access complete user profile data including company roles and workspace roles
 */
export const useUserProfile = (): UseUserProfileReturn => {
  const { userProfile, userCompanyRoleSets, allWorkspaces, homeClientCompanyId, canManageCustomers } = useAuth()
  const location = useLocation()

  // Extract current company ID from URL
  const getCurrentCompanyId = (): string | null => {
    try {
      const match = location?.path?.match(/\/company\/([^/]+)/)
      return match?.[1] || null
    } catch {
      return null
    }
  }

  // Get role set for current company from URL
  const getCurrentCompanyRoleSet = (): UserCompanyRoleSetViewDto | null => {
    const currentCompanyId = getCurrentCompanyId()
    if (!currentCompanyId || !userCompanyRoleSets) return null
    return userCompanyRoleSets.find(rs => rs.ClientCompanyId === currentCompanyId) || null
  }

  // Get workspaces for current company
  const getCurrentCompanyWorkspaces = (): ClientWorkspaceSummaryViewDto[] => {
    const currentCompanyId = getCurrentCompanyId()
    if (!currentCompanyId || !allWorkspaces) return []
    return allWorkspaces.filter(w => w.ClientCompanyId === currentCompanyId)
  }

  // Check if user has a specific role (company-wide or in a workspace)
  const isUserInRole = (role: RoleType, companyId: string, workspaceId?: string): boolean => {
    const roleSet = userCompanyRoleSets?.find(rs => rs.ClientCompanyId === companyId)
    if (!roleSet) return false

    // Check company-wide roles
    if (!workspaceId) {
      return roleSet.CompanyRoles.includes(role)
    }

    // Check workspace-specific roles
    const workspaceRoles = roleSet.WorkspaceRoles.find((wr: WorkspaceRolesViewDto) => wr.WorkspaceId === workspaceId)
    return workspaceRoles?.Roles.includes(role) || false
  }

  // Get all roles for user (optionally filtered by workspace)
  const getUserRoles = (companyId: string, workspaceId?: string): RoleType[] => {
    const roleSet = userCompanyRoleSets?.find(rs => rs.ClientCompanyId === companyId)
    if (!roleSet) return []

    if (!workspaceId) {
      // Return company-wide roles
      return roleSet.CompanyRoles
    }

    // Return workspace-specific roles
    const workspaceRoles = roleSet.WorkspaceRoles.find((wr: WorkspaceRolesViewDto) => wr.WorkspaceId === workspaceId)
    return workspaceRoles?.Roles || []
  }

  // Check if user has a company-wide role
  const hasCompanyRole = (role: RoleType, companyId: string): boolean => {
    const roleSet = userCompanyRoleSets?.find(rs => rs.ClientCompanyId === companyId)
    return roleSet?.CompanyRoles.includes(role) || false
  }

  // Check if user has a role in a specific workspace
  const hasWorkspaceRole = (role: RoleType, workspaceId: string, companyId: string): boolean => {
    const roleSet = userCompanyRoleSets?.find(rs => rs.ClientCompanyId === companyId)
    if (!roleSet) return false
    const workspaceRoles = roleSet.WorkspaceRoles.find((wr: WorkspaceRolesViewDto) => wr.WorkspaceId === workspaceId)
    return workspaceRoles?.Roles.includes(role) || false
  }

  // Get workspace by ID
  const getWorkspaceById = (workspaceId: string): ClientWorkspaceSummaryViewDto | undefined => {
    return allWorkspaces?.find(ws => ws.Id === workspaceId)
  }

  // Get all workspaces the user has roles in for a specific company
  const getUserWorkspaces = (companyId: string): ClientWorkspaceSummaryViewDto[] => {
    const roleSet = userCompanyRoleSets?.find(rs => rs.ClientCompanyId === companyId)
    if (!roleSet || !allWorkspaces) return []

    const userWorkspaceIds = roleSet.WorkspaceRoles.map((wr: WorkspaceRolesViewDto) => wr.WorkspaceId)
    return allWorkspaces.filter(ws => ws.ClientCompanyId === companyId && userWorkspaceIds.includes(ws.Id))
  }

  // Get workspace capabilities (two-tier: company admin first, then workspace-specific flags)
  const getWorkspaceCapabilities = (clientCompanyId: string, workspaceId: string): { canAdmin: boolean; canEdit: boolean; canView: boolean } => {
    const roleSet = userCompanyRoleSets?.find(rs => rs.ClientCompanyId === clientCompanyId)
    if (!roleSet) return { canAdmin: false, canEdit: false, canView: false }
    
    // First tier: Check if user is company-level admin
    if (roleSet.IsAdmin) {
      return { canAdmin: true, canEdit: true, canView: true }
    }
    
    // Second tier: Check workspace-specific roles (server-computed flags)
    const workspaceRoles = roleSet.WorkspaceRoles.find((wr: WorkspaceRolesViewDto) => wr.WorkspaceId === workspaceId)
    return {
      canAdmin: workspaceRoles?.CanAdmin || false,
      canEdit: workspaceRoles?.CanEdit || false,
      canView: workspaceRoles?.CanView || false
    }
  }

  return {
    userProfile,
    userCompanyRoleSets,
    allWorkspaces,
    homeClientCompanyId,
    canManageCustomers,
    getCurrentCompanyRoleSet,
    getCurrentCompanyWorkspaces,
    isUserInRole,
    getUserRoles,
    hasCompanyRole,
    hasWorkspaceRole,
    getWorkspaceCapabilities,
    getWorkspaceById,
    getUserWorkspaces,
  }
}

// Common role constants you can use throughout your app
export const USER_ROLES = {
  // Available role types from backend
  VIEWER: 'Viewer' as const,
  EDITOR: 'Editor' as const,
  ADMIN: 'Admin' as const,
} as const

export type UserRole = typeof USER_ROLES[keyof typeof USER_ROLES]
